import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

type Vendor = 'smartlink' | 'chipmind';

interface PushRow {
  id: number;
  endpoint: string;
  payload: unknown;
  response: unknown;
  success: boolean;
  httpStatus: number | null;
  errorMessage: string | null;
  attemptedAt: string;
}

interface LogResponse {
  rows: PushRow[];
}

const VENDOR_LABEL: Record<Vendor, string> = {
  smartlink: 'SmartLink',
  chipmind: 'ChipMind',
};

const EXAMPLE_PATHS: Record<Vendor, string[]> = {
  smartlink: [
    '/open-api/dept/push',
    '/open-api/base-product/third-push',
    '/open-api/shop-sale-order/push',
    '/open-api/warehouse-outbound-order/push',
    '/open-api/warehouse-inbound-order/push',
  ],
  chipmind: [
    '/open/brandBaseData/createBaseData',
    '/open/tagPurchaseOrder/createTagPurchaseOrderInfo',
  ],
};

export function AdminVendorLogPage() {
  const [vendor, setVendor] = useState<Vendor>('smartlink');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'vendor', vendor],
    queryFn: () => apiFetch<LogResponse>(`/api/admin/vendor/log?vendor=${vendor}`),
    refetchInterval: 30_000,
  });

  const queue = useQuery({
    queryKey: ['admin', 'vendor', 'queue'],
    queryFn: () =>
      apiFetch<Record<Vendor, { pending: number; sending: number; failed: number; done: number }>>(
        '/api/admin/vendor/queue',
      ),
    refetchInterval: 15_000,
  });

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Vendor integrations</h1>
        <span className="main-subtitle">SmartLink + ChipMind outbound calls</span>
      </header>
      <Link to="/admin" className="back-link">← Admin</Link>

      <div className="toolbar" style={{ gap: '0.4rem' }}>
        {(['smartlink', 'chipmind'] as Vendor[]).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVendor(v)}
            style={{
              background: vendor === v ? 'var(--accent)' : 'var(--surface-2)',
              color: vendor === v ? 'var(--bg)' : 'var(--text)',
              border: '1px solid var(--border)',
              padding: '0.4rem 0.9rem',
              cursor: 'pointer',
              fontSize: 13,
              borderRadius: 2,
              fontWeight: vendor === v ? 600 : 400,
            }}
          >
            {VENDOR_LABEL[v]}
          </button>
        ))}
      </div>

      {queue.data && (
        <section className="panel">
          <header className="panel-header">
            <h2 className="panel-title">Push queue — {VENDOR_LABEL[vendor]}</h2>
            <span className="panel-subtitle">worker runs every 60s</span>
          </header>
          <div className="kpi-grid">
            <QueueKPI label="Pending" value={queue.data[vendor].pending} />
            <QueueKPI label="Sending" value={queue.data[vendor].sending} />
            <QueueKPI label="Failed" value={queue.data[vendor].failed} flag={queue.data[vendor].failed > 0} />
            <QueueKPI label="Done" value={queue.data[vendor].done} />
          </div>
          {vendor === 'chipmind' && (
            <BackfillProductsButton
              onSeeded={() => {
                qc.invalidateQueries({ queryKey: ['admin', 'vendor', 'queue'] });
                qc.invalidateQueries({ queryKey: ['admin', 'vendor', vendor] });
              }}
            />
          )}
        </section>
      )}

      <TestFirePanel
        vendor={vendor}
        onFired={() => qc.invalidateQueries({ queryKey: ['admin', 'vendor', vendor] })}
      />

      <section className="panel">
        <header className="panel-header">
          <h2 className="panel-title">Recent {VENDOR_LABEL[vendor]} calls</h2>
          <span className="panel-subtitle">
            {data ? `${data.rows.length} most recent · refreshes every 30s` : ''}
          </span>
        </header>
        {isLoading && <div className="loading">Loading…</div>}
        {error && <div className="error">{(error as Error).message}</div>}
        {data && data.rows.length === 0 && (
          <div className="empty">No calls logged yet. Fire one via the panel above.</div>
        )}
        {data && data.rows.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Endpoint</th>
                <th>Status</th>
                <th className="num">HTTP</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    {dateTime(r.attemptedAt)}
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.endpoint}</td>
                  <td>
                    <span className={`badge ${r.success ? 'badge-ok' : 'badge-cancel'}`}>
                      {r.success ? 'success' : 'fail'}
                    </span>
                  </td>
                  <td className="num">{r.httpStatus ?? '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {r.errorMessage ?? (r.response ? JSON.stringify(r.response).slice(0, 120) : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function TestFirePanel({
  vendor,
  onFired,
}: {
  vendor: Vendor;
  onFired: () => void;
}) {
  const [path, setPath] = useState(EXAMPLE_PATHS[vendor][0] ?? '');
  const [payload, setPayload] = useState('{}');
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<unknown>(null);

  const fire = useMutation({
    mutationFn: async () => {
      let parsedPayload: unknown;
      try {
        parsedPayload = payload.trim() === '' ? {} : JSON.parse(payload);
      } catch (e) {
        setParseError('Payload is not valid JSON.');
        throw e;
      }
      setParseError(null);
      return apiFetch('/api/admin/vendor/dispatch', {
        method: 'POST',
        body: JSON.stringify({ vendor, path, payload: parsedPayload }),
      });
    },
    onSuccess: (r) => {
      setResult(r);
      onFired();
    },
  });

  // Refresh path suggestion when vendor changes
  if (path && !EXAMPLE_PATHS[vendor].includes(path) && path === EXAMPLE_PATHS[vendor === 'smartlink' ? 'chipmind' : 'smartlink'][0]) {
    setPath(EXAMPLE_PATHS[vendor][0] ?? '');
  }

  return (
    <section className="panel">
      <header className="panel-header">
        <h2 className="panel-title">Test fire — {VENDOR_LABEL[vendor]}</h2>
        <span className="panel-subtitle">manual dispatch with full signing</span>
      </header>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={labelStyle}>Path</span>
          <input
            list={`paths-${vendor}`}
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/open-api/dept/push"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
          <datalist id={`paths-${vendor}`}>
            {EXAMPLE_PATHS[vendor].map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={labelStyle}>JSON payload</span>
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            rows={8}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
          />
        </label>
        {parseError && <div className="error">{parseError}</div>}
        <div>
          <button
            type="button"
            onClick={() => fire.mutate()}
            disabled={fire.isPending || !path}
            style={{
              background: 'var(--accent)',
              color: 'var(--bg)',
              border: 'none',
              padding: '0.55rem 1rem',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: fire.isPending ? 0.6 : 1,
            }}
          >
            {fire.isPending ? 'Firing…' : `POST to ${VENDOR_LABEL[vendor]}`}
          </button>
        </div>
        {fire.error && <div className="error">{(fire.error as Error).message}</div>}
        {result != null && (
          <pre
            style={{
              background: 'var(--surface-2)',
              padding: '0.75rem',
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              maxHeight: 240,
              overflow: 'auto',
              margin: 0,
              border: '1px solid var(--border)',
            }}
          >
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </section>
  );
}

function BackfillProductsButton({ onSeeded }: { onSeeded: () => void }) {
  const [result, setResult] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      apiFetch<{ enqueued: number }>('/api/admin/vendor/backfill-products', {
        method: 'POST',
      }),
    onSuccess: (r) => {
      setResult(`Enqueued ${r.enqueued} products. Worker will drain at 100/min.`);
      onSeeded();
    },
    onError: (e) => setResult(`Failed: ${(e as Error).message}`),
  });
  return (
    <div style={{ marginTop: '0.75rem' }}>
      <button
        type="button"
        onClick={() => {
          if (confirm('Enqueue every active product into ChipMind push queue?')) mut.mutate();
        }}
        disabled={mut.isPending}
        style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          color: 'var(--text)',
          padding: '0.5rem 0.9rem',
          cursor: 'pointer',
          fontSize: 12,
          borderRadius: 2,
        }}
      >
        {mut.isPending ? 'Enqueueing…' : 'One-time backfill: enqueue all products'}
      </button>
      {result && (
        <div style={{ marginTop: '0.4rem', fontSize: 12, color: 'var(--text-muted)' }}>{result}</div>
      )}
    </div>
  );
}

function QueueKPI({ label, value, flag }: { label: string; value: number; flag?: boolean }) {
  return (
    <div className="kpi-card" style={flag ? { borderColor: 'var(--danger, #ff6b6b)' } : undefined}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};
